import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import differenceInSeconds from 'date-fns/differenceInSeconds';
import recordEvent from 'platform/monitoring/record-event';
import { logout as IAMLogout } from 'platform/user/authentication/utilities';
import { refresh, logoutUrlSiS } from 'platform/utilities/oauth/utilities';
import { OAUTH_KEYS } from 'platform/utilities/oauth/constants';
import { teardownProfileSession } from 'platform/user/profile/utilities';
import localStorage from 'platform/utilities/storage/localStorage';
import Modal from '@department-of-veterans-affairs/component-library/Modal';

import { initializeProfile } from 'platform/user/profile/actions';
import {
  signInServiceName as signInServiceNameSelector,
  isAuthenticatedWithOAuth,
} from 'platform/user/authentication/selectors';
import { isLoggedIn as LoginSelector } from 'platform/user/selectors';

const MODAL_DURATION = 30; // seconds
const INTERVAL_DURATION = 1000; // milliseconds

export const SessionTimeoutModal = ({
  onExtendSession,
  authenticatedWithOAuth,
  isLoggedIn,
  state = { countdown: null },
  expirationInterval = null,
  serviceName = '',
}) => {
  const [countdownState, setCountdownState] = useState(state.countdown);
  const intervalId = useRef(expirationInterval);
  const modalVisible = useRef(false);

  const restoreInterval = () => {
    clearInterval(intervalId.current);
    intervalId.current = null;
  };

  const expireSession = useCallback(() => {
    recordEvent({ event: 'logout-session-expired' });
    teardownProfileSession();
    if (!authenticatedWithOAuth) {
      IAMLogout();
    } else {
      window.location = logoutUrlSiS({
        queryParams: { [OAUTH_KEYS.LOGOUT_TYPE]: 'idle' },
      });
    }
  }, [authenticatedWithOAuth]);

  const checkExpiration = useCallback(() => {
    if (!isLoggedIn) {
      restoreInterval();
      return;
    }

    const expirationDate = localStorage.getItem('sessionExpiration');
    if (!expirationDate || Number.isNaN(new Date(expirationDate).getTime()))
      return;

    const countdown = differenceInSeconds(new Date(expirationDate), Date.now());
    if (countdown < 0) {
      expireSession();
    } else if (countdown <= MODAL_DURATION) {
      if (!modalVisible.current) {
        recordEvent({ event: 'session-timeout-modal-displayed' });
        modalVisible.current = true;
      }
      setCountdownState(countdown);
    }
  }, [expireSession, isLoggedIn]);

  const extendSession = () => {
    recordEvent({ event: 'login-cta-stay-signed-in' });

    // Remove session expiration to temporarily prevent the interval from
    // overwriting countdown immediately after clearing it.
    // Expiration will reset after a successful request to extend the session.
    localStorage.removeItem('sessionExpiration');
    setCountdownState(null);
    modalVisible.current = false;
    if (authenticatedWithOAuth) {
      refresh({ type: serviceName });
    } else {
      onExtendSession();
    }
  };

  const signOut = () => {
    recordEvent({ event: 'logout-cta-manual-signout' });
    if (!authenticatedWithOAuth) {
      IAMLogout();
    } else {
      window.location = logoutUrlSiS({
        queryParams: { [OAUTH_KEYS.LOGOUT_TYPE]: 'user' },
      });
    }
  };

  useEffect(() => {
    if (isLoggedIn && !intervalId.current) {
      restoreInterval();
      intervalId.current = setInterval(checkExpiration, INTERVAL_DURATION);
    }

    return restoreInterval;
  }, [checkExpiration, isLoggedIn]);

  return (
    <Modal
      hideCloseButton
      id="session-timeout-modal"
      focusSelector="button"
      onClose={extendSession}
      status="warning"
      title="Your session will end in..."
      visible={countdownState} // Display only for 30s countdown.
    >
      {/* React Modal Component used in Injected Header DO NOT MIGRATE to Modal web component */}
      <div className="vads-u-text-align--center">
        <div className="vads-u-font-size--2xl">{countdownState}</div>
        <div>SECONDS</div>
      </div>
      <p>
        If you need more time, please click I need more time below. Otherwise,
        we’ll sign you out of your account to protect your privacy.
      </p>
      <div className="alert-actions">
        <button type="button" className="usa-button" onClick={extendSession}>
          I need more time
        </button>
        <button type="button" className="va-button-link" onClick={signOut}>
          Sign out
        </button>
      </div>
    </Modal>
  );
};

export const mapStateToProps = state => {
  return {
    isLoggedIn: LoginSelector(state),
    authenticatedWithOAuth: isAuthenticatedWithOAuth(state),
    serviceName:
      signInServiceNameSelector(state) || sessionStorage.getItem('ci') || '',
  };
};

const mapDispatchToProps = {
  onExtendSession: initializeProfile,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SessionTimeoutModal);

SessionTimeoutModal.propTypes = {
  onExtendSession: PropTypes.func.isRequired,
  authenticatedWithOAuth: PropTypes.bool,
  expirationInterval: PropTypes.number,
  isLoggedIn: PropTypes.bool,
  serviceName: PropTypes.string,
  state: PropTypes.object,
};
